import type { ToolCallExecution } from "@webuild-ai/admin/models/components";
import type { Workflow } from "./WorkflowStore";
import type { UploadedFile } from "./ChatStore";

import { create } from "zustand";

export type NodeStatus = "idle" | "running" | "success" | "error";

export interface PlaygroundMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  status: "success" | "failed" | "streaming" | "stopped";
  agent?: string;
  toolCallExecutions?: ToolCallExecution[];
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface WorkflowProgress {
  totalSteps: number;
  completedSteps: number;
  currentStep: string | null;
  stepOrder: string[];
  executionOrder: string[];
}

export interface PlaygroundStore {
  // Canvas state
  selectedNodeId: string | null;
  isCanvasReady: boolean;

  // Dirty tracking
  originalWorkflowSnapshot: string | null;
  hasUnsavedChanges: boolean;

  // Execution state
  isRunning: boolean;
  executionMessages: PlaygroundMessage[];
  nodeStatuses: Record<string, NodeStatus>;
  workflowProgress: WorkflowProgress;

  // UI state
  isPropertiesPanelOpen: boolean;
  isMessagesPanelOpen: boolean;

  // File uploads
  uploadedFiles: UploadedFile[];

  // Validation
  validationErrors: string[];
  nodeValidationErrors: Record<string, string[]>;
  isValidating: boolean;

  // Actions
  setSelectedNode: (nodeId: string | null) => void;
  setCanvasReady: (ready: boolean) => void;
  snapshotWorkflow: (workflow: Workflow | null) => void;
  checkUnsavedChanges: (currentWorkflow: Workflow | null) => void;
  markClean: () => void;

  // Execution actions
  setIsRunning: (running: boolean) => void;
  resetExecution: () => void;
  updateNodeStatus: (nodeId: string, status: NodeStatus) => void;
  resetNodeStatuses: (nodeIds: string[]) => void;
  addMessage: (message: PlaygroundMessage) => void;
  updateMessage: (messageId: string, updates: Partial<PlaygroundMessage>) => void;
  clearMessages: () => void;
  setWorkflowProgress: (progress: Partial<WorkflowProgress>) => void;

  // UI actions
  togglePropertiesPanel: () => void;
  setPropertiesPanelOpen: (open: boolean) => void;
  toggleMessagesPanel: () => void;
  setMessagesPanelOpen: (open: boolean) => void;

  // File upload actions
  setUploadedFiles: (files: UploadedFile[]) => void;
  clearUploadedFiles: () => void;

  // Validation actions
  setValidationErrors: (errors: string[]) => void;
  setNodeValidationErrors: (errors: Record<string, string[]>) => void;
  setIsValidating: (validating: boolean) => void;

  // Reset
  reset: () => void;
}

const initialProgress: WorkflowProgress = {
  totalSteps: 0,
  completedSteps: 0,
  currentStep: null,
  stepOrder: [],
  executionOrder: [],
};

export const usePlaygroundStore = create<PlaygroundStore>((set, get) => ({
  // Initial state
  selectedNodeId: null,
  isCanvasReady: false,
  originalWorkflowSnapshot: null,
  hasUnsavedChanges: false,
  isRunning: false,
  executionMessages: [],
  nodeStatuses: {},
  workflowProgress: initialProgress,
  isPropertiesPanelOpen: false,
  isMessagesPanelOpen: false,
  uploadedFiles: [],
  validationErrors: [],
  nodeValidationErrors: {},
  isValidating: false,

  // Canvas actions
  setSelectedNode: (nodeId) => {
    set({ selectedNodeId: nodeId, isPropertiesPanelOpen: nodeId !== null });
  },

  setCanvasReady: (ready) => set({ isCanvasReady: ready }),

  // Dirty tracking
  snapshotWorkflow: (workflow) => {
    set({
      originalWorkflowSnapshot: workflow ? JSON.stringify(workflow) : null,
      hasUnsavedChanges: false,
    });
  },

  checkUnsavedChanges: (currentWorkflow) => {
    const { originalWorkflowSnapshot } = get();
    const currentSnapshot = currentWorkflow ? JSON.stringify(currentWorkflow) : null;

    set({ hasUnsavedChanges: currentSnapshot !== originalWorkflowSnapshot });
  },

  markClean: () => set({ hasUnsavedChanges: false }),

  // Execution actions
  setIsRunning: (running) => set({ isRunning: running }),

  resetExecution: () =>
    set({
      isRunning: false,
      executionMessages: [],
      nodeStatuses: {},
      workflowProgress: initialProgress,
    }),

  updateNodeStatus: (nodeId, status) =>
    set((state) => ({
      nodeStatuses: { ...state.nodeStatuses, [nodeId]: status },
    })),

  resetNodeStatuses: (nodeIds) =>
    set({
      nodeStatuses: Object.fromEntries(nodeIds.map((id) => [id, "idle" as NodeStatus])),
    }),

  addMessage: (message) =>
    set((state) => ({
      executionMessages: [...state.executionMessages, message],
    })),

  updateMessage: (messageId, updates) =>
    set((state) => ({
      executionMessages: state.executionMessages.map((msg) =>
        msg.id === messageId ? { ...msg, ...updates } : msg,
      ),
    })),

  clearMessages: () => set({ executionMessages: [] }),

  setWorkflowProgress: (progress) =>
    set((state) => ({
      workflowProgress: { ...state.workflowProgress, ...progress },
    })),

  // UI actions
  togglePropertiesPanel: () =>
    set((state) => ({ isPropertiesPanelOpen: !state.isPropertiesPanelOpen })),

  setPropertiesPanelOpen: (open) => set({ isPropertiesPanelOpen: open }),

  toggleMessagesPanel: () => set((state) => ({ isMessagesPanelOpen: !state.isMessagesPanelOpen })),

  setMessagesPanelOpen: (open) => set({ isMessagesPanelOpen: open }),

  // File upload actions
  setUploadedFiles: (files) => set({ uploadedFiles: files }),
  clearUploadedFiles: () => set({ uploadedFiles: [] }),

  // Validation actions
  setValidationErrors: (errors) => set({ validationErrors: errors }),

  setNodeValidationErrors: (errors) => set({ nodeValidationErrors: errors }),

  setIsValidating: (validating) => set({ isValidating: validating }),

  // Reset
  reset: () =>
    set({
      selectedNodeId: null,
      isCanvasReady: false,
      originalWorkflowSnapshot: null,
      hasUnsavedChanges: false,
      isRunning: false,
      executionMessages: [],
      nodeStatuses: {},
      workflowProgress: initialProgress,
      isPropertiesPanelOpen: false,
      isMessagesPanelOpen: false,
      uploadedFiles: [],
      validationErrors: [],
      nodeValidationErrors: {},
      isValidating: false,
    }),
}));
